import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api } from "../lib/api";
import { useSocket } from "./SocketContext";
import { playEmergencyTones } from "../lib/emergencyAudio";

const SafetyAlertContext = createContext({ events: [], count: 0, affectsMeCount: 0, reload: () => {}, markSafe: () => {} });

export function SafetyAlertProvider({ children }) {
  const [events, setEvents] = useState([]);
  const { subscribe } = useSocket();
  const playedRef = useRef(new Set());

  const reload = useCallback(async () => {
    try { const { data } = await api.get("/mystic/safety/all-active"); setEvents(data.events || []); }
    catch { /* noop */ }
  }, []);

  useEffect(() => { reload(); const id = setInterval(reload, 30000); return () => clearInterval(id); }, [reload]);

  useEffect(() => subscribe((m) => {
    if (m?.type === "safety_active" || m?.type === "safety_alert" || m?.type === "safety_clear") reload();
    if (m?.type === "safety_reminder") { playEmergencyTones(); reload(); }  // an Alpha nudged me to check in
  }), [subscribe, reload]);

  // Autoplay the emergency tone the moment a new alert affecting me appears (unconfirmed).
  useEffect(() => {
    for (const e of events) {
      if (e.affects_me && !e.i_am_safe && !playedRef.current.has(e.id)) {
        playedRef.current.add(e.id);
        playEmergencyTones();
        break;
      }
    }
  }, [events]);

  const markSafe = useCallback(async (id) => {
    await api.post(`/mystic/safety/${id}/safe`);
    setEvents((p) => p.map((e) => (e.id === id ? { ...e, i_am_safe: true } : e)));
  }, []);

  const value = useMemo(() => ({
    events,
    count: events.length,
    affectsMeCount: events.filter((e) => e.affects_me).length,
    reload,
    markSafe,
  }), [events, reload, markSafe]);

  return <SafetyAlertContext.Provider value={value}>{children}</SafetyAlertContext.Provider>;
}

export const useSafetyAlerts = () => useContext(SafetyAlertContext);
